import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { RestServiceConstants } from "../../constants/rest-service.constants";
import { RestClient } from "../rest/client/rest.client";
import { ResponseBody } from "../rest/decorators/response-body.decorator";
import { Response } from "../rest/decorators/response.decorator";
import { RequestBody, Query, PlainBody } from "../rest/request/params";
import { Headers } from "../rest/decorators/headers.decorator";
import { DELETE, GET, POST, PUT } from "../rest/request/methods";
import { StringUtil } from "../../utils/string.util";
import { AppConfigUtil } from "../../utils/app-config.util";
import { FundingToken } from "src/app/models/dmv/fundingToken";

import { HttpClient, HttpResponse } from "@angular/common/http";
import { USERConstants } from "src/app/constants/user.constants";
import {
	ContactInfo,
	CreatedUserResponse,
	Credentials,
	EmailAddress,
	Password
} from "src/app/models/user/models";
import { LogInResponse } from "src/app/models/user/log-in.response";
import { BaseApiResponse } from "src/app/models/user/base-api.response";
import { RegistrationResponse } from "src/app/models/dmv/registration.response";
import { NotificationSettings } from "src/app/models/user/notificationSettings";
import { BillingAccount } from "src/app/models/user/billingAccount";
import { BillingAccounts } from "src/app/models/user/billingAccounts";
import { PaymentDeviceResponse } from "src/app/models/user/payment-device.response";
import { AccountCancellationInstructions } from "src/app/models/user/accountCancellationInstructions";
import { AccountCancellationResponse } from "src/app/models/user/accountCancellationResponse";

@Injectable({ providedIn: "root" })
export class USERDataService extends RestClient {
	@Headers({
		Accept: RestServiceConstants.APPLICATION_JSON,
		"content-type": RestServiceConstants.APPLICATION_JSON
	})
	@POST()
	@ResponseBody<any>()
	public createAccount(
		@RequestBody request: ContactInfo
	): Observable<LogInResponse> {
		return;
	}

	public createAccountResponse(
		sessionGUID: string,
		request
	): Observable<LogInResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(USERConstants.USER_ACCOUNT, sessionGUID)
		);

		return this.createAccount(request);
	}

	@POST()
	@ResponseBody<any>()
	public login(@RequestBody request: Credentials): Observable<any> {
		return;
	}

	public callLoginApi(sessionGUID: string, request): Observable<LogInResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(StringUtil.format(USERConstants.LOG_IN, sessionGUID));

		return this.login(request);
	}

	@PUT()
	@ResponseBody<any>()
	public changePassword(@RequestBody request: Password): Observable<any> {
		return;
	}

	public callChangePassswordApi(
		sessionGUID: string,
		megUserGUID: string,
		request
	): Observable<BaseApiResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(USERConstants.PASSWORD, sessionGUID, megUserGUID)
		);

		return this.changePassword(request);
	}
	@PUT()
	@ResponseBody<any>()
	public changeEmail(@RequestBody request: EmailAddress): Observable<any> {
		return;
	}

	public callChangeEmailAddressApi(
		sessionGUID: string,
		megUserGUID: string,
		request
	): Observable<BaseApiResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(USERConstants.EMAILADDRESS, sessionGUID, megUserGUID)
		);

		return this.changeEmail(request);
	}

	@PUT()
	@ResponseBody<any>()
	public notificationSettings(
		@RequestBody request: NotificationSettings
	): Observable<any> {
		return;
	}

	public callNotificationsApi(
		sessionGUID: string,
		megUserGUID: string,
		request
	): Observable<LogInResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				USERConstants.NOTIFICATION_SETTINGS,
				sessionGUID,
				megUserGUID
			)
		);

		return this.notificationSettings(request);
	}

	@PUT()
	@ResponseBody<any>()
	public contactInfo(@RequestBody request: ContactInfo): Observable<any> {
		return;
	}

	public callContactInfoApi(
		sessionGUID: string,
		megUserGUID: string,
		request
	): Observable<LogInResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(USERConstants.CONTACT_INFO, sessionGUID, megUserGUID)
		);

		return this.contactInfo(request);
	}

	@GET()
	@ResponseBody<any>()
	public registrations(
		@Query("megUserGUID") megUserGUID?: string,
		@Query("pageSize") pageSize?: number
	): Observable<RegistrationResponse> {
		return;
	}

	public getRegistraions(
		selectedAgencyId: string,
		sessionID: string,
		megUserGUID: string,
		pageSize: number
	): Observable<RegistrationResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				USERConstants.REGISTRATIONS,
				selectedAgencyId,
				sessionID
			)
		);

		return this.registrations(megUserGUID, pageSize);
	}

	@GET()
	@ResponseBody<any>()
	public paymentDevice(): Observable<PaymentDeviceResponse> {
		return;
	}

	public getPaymentDevices(
		selectedAgencyId: string,
		sessionID: string
	): Observable<PaymentDeviceResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				USERConstants.GETPAYMENTDEVICE,
				selectedAgencyId,
				sessionID
			)
		);

		return this.paymentDevice();
	}

	@DELETE()
	@ResponseBody<any>()
	public deletePaymentDevice(): Observable<LogInResponse> {
		return;
	}

	public deletePaymentDeviceCall(
		sessionID: string,
		megUserGUID: string,
		fundingToken: string
	): Observable<LogInResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				USERConstants.PAYMENT_DEVICE,
				sessionID,
				megUserGUID,
				fundingToken
			)
		);

		return this.deletePaymentDevice();
	}

	@PUT()
	@ResponseBody<any>()
	public editPaymentDevice(
		@RequestBody request: any
	): Observable<LogInResponse> {
		return;
	}

	public editPaymentDeviceCall(
		sessionID: string,
		megUserGUID: string,
		fundingToken: string,
		shortName: string
	): Observable<LogInResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				USERConstants.PAYMENT_DEVICE,
				sessionID,
				megUserGUID,
				fundingToken
			)
		);

		return this.editPaymentDevice({ shortName: shortName });
	}

	@DELETE()
	@ResponseBody<any>()
	public billingAccount(): Observable<LogInResponse> {
		return;
	}

	public deleteBillingAccount(
		sessionID: string,
		megUserGUID: string,
		billingAccountID: number
	): Observable<LogInResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				USERConstants.BILLING_ACCOUNT,
				sessionID,
				megUserGUID,
				billingAccountID
			)
		);

		return this.billingAccount();
	}

	@POST()
	@ResponseBody<any>()
	public addRegistartion(
		@RequestBody request: BillingAccount,
		@Query("OptForReminder") optForReminder: string
	): Observable<LogInResponse> {
		return;
	}

	public addBillingAccount(
		sessionID: string,
		megUserGUID: string,
		request: BillingAccount,
		optForReminder: string
	): Observable<LogInResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				USERConstants.ADD_BILLING_ACCOUNT,
				sessionID,
				megUserGUID
			)
		);

		return this.addRegistartion(request, optForReminder);
	}

	@POST()
	@ResponseBody<any>()
	public logout(): Observable<BaseApiResponse> {
		return;
	}

	public callLogoutApi(
		sessionGUID: string,
		megUserGUID: string
	): Observable<BaseApiResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(USERConstants.LOG_OUT, sessionGUID, megUserGUID)
		);

		return this.logout();
	}

	@POST()
	@ResponseBody<any>()
	public resetPassword(): Observable<BaseApiResponse> {
		return;
	}

	public callResetPasswordApi(
		sessionGUID: string,
		emailAddress: string
	): Observable<BaseApiResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(USERConstants.RESET_PASSWORD, sessionGUID, emailAddress)
		);

		return this.resetPassword();
	}

	@POST()
	@Response<any>()
	public accountCancellationApi(): Observable<
		HttpResponse<AccountCancellationInstructions>
	> {
		return;
	}

	public accountCancellation(
		sessionGUID: string,
		token: string
	): Observable<HttpResponse<AccountCancellationInstructions>> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(USERConstants.ACCOUNT_CANCELLATION, sessionGUID, token)
		);

		return this.accountCancellationApi();
	}

	@PUT()
	@Response<any>()
	public accountCancellationconfirmedApi(): Observable<
		HttpResponse<AccountCancellationResponse>
	> {
		return;
	}

	public accountCancellationconfirmed(
		sessionGUID: string,
		confirmed: string
	): Observable<HttpResponse<AccountCancellationResponse>> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				USERConstants.ACCOUNT_CANCELLATION_CONFIRMED,
				sessionGUID,
				confirmed
			)
		);

		return this.accountCancellationconfirmedApi();
	}
}
